import { STYLES, REFERENCES } from "../constants";
import type { IPromptBoxMessages, IActionButtonSettings } from "../models";
import type { IKendoJQuery } from "../../core";

declare const kendo: any;

export interface IActionButtonManagerContext {
    wrapper: IKendoJQuery;
    getMessages: () => IPromptBoxMessages;
    getSettings: () => IActionButtonSettings | undefined;
    isDisabled: () => boolean;
    isLoading: () => boolean;
    hasContent: () => boolean;
}

export class ActionButtonManager {
    private context: IActionButtonManagerContext;

    constructor(context: IActionButtonManagerContext) {
        this.context = context;
    }

    private getSendButton(): JQuery {
        return this.context.wrapper.find(`[${REFERENCES.sendButton}]`);
    }

    setLoading(loading: boolean): void {
        const sendButton = this.getSendButton();
        const messages = this.context.getMessages();
        const settings = this.context.getSettings();

        if (loading) {
            sendButton.addClass(`${STYLES.generating} ${STYLES.active}`);
            sendButton.attr("title", messages.actionButtonLoading);
            sendButton.attr("aria-label", messages.actionButtonLoading);
            const icon = sendButton.find(".k-icon, .k-svg-icon");
            if (icon.length) {
                const loadingIcon = settings?.loadingIcon || "stop";
                kendo.ui.icon(icon, { icon: loadingIcon });
            }
        } else {
            sendButton.removeClass(`${STYLES.generating} ${STYLES.active}`);
            sendButton.attr("title", messages.actionButton);
            sendButton.attr("aria-label", messages.actionButton);
            const icon = sendButton.find(".k-icon, .k-svg-icon");
            if (icon.length) {
                const sendIcon = settings?.icon || "arrow-up";
                kendo.ui.icon(icon, { icon: sendIcon });
            }
        }

        this.updateState();
    }

    updateState(): void {
        const sendButton = this.getSendButton();
        const isDisabled = this.context.isDisabled();

        if (isDisabled) {
            sendButton.addClass(STYLES.disabled);
            sendButton.attr("aria-disabled", "true");
            return;
        }

        sendButton.removeAttr("aria-disabled");

        if (this.context.isLoading()) {
            sendButton.removeClass(STYLES.disabled);
            return;
        }

        if (this.context.hasContent()) {
            sendButton.removeClass(STYLES.disabled);
        } else {
            sendButton.addClass(STYLES.disabled);
        }
    }

    updateMessages(): void {
        const sendButton = this.getSendButton();
        const messages = this.context.getMessages();

        if (this.context.isLoading()) {
            sendButton.attr("title", messages.actionButtonLoading);
            sendButton.attr("aria-label", messages.actionButtonLoading);
        } else {
            sendButton.attr("title", messages.actionButton);
            sendButton.attr("aria-label", messages.actionButton);
        }
    }
}
